/**
 * Wave 354 · Autonomy Ladder L0–L9.
 *
 * Single integer scale that classifies how autonomous a capability or
 * proposal is. Higher = more independent of a human, smaller pool of
 * surfaces allowed there. Distinct from the existing 0–7 *maturity*
 * ladder rendered by AdminBrainPage's `MaturityPanel` — that panel
 * measures shipping/observability completeness; this scale measures
 * autonomy. Both live in parallel.
 *
 * Locked semantics (Wave 354 spec §3.1):
 *   L0 — Observe                Housy reads, never acts
 *   L1 — Recommend               Housy suggests in chat/bubble/digest
 *   L2 — Draft                   Prumo/shared (or user-initiated Housy
 *                                allowlisted) creates `status='draft'`
 *                                rows the user must explicitly sign
 *   L3 — Propose · high-blast    Prumo creates pm_proposals row;
 *                                human approval; never auto
 *   L4 — Propose · medium-blast  Prumo proposal; human approval
 *   L5 — Propose · low-blast     Prumo proposal; human approval (default)
 *                                or rule pre-auth (becomes L6 only after
 *                                Wave 354 R2 wires the enforcement chain)
 *   L6 — Auto-execute (rule)     Prumo executor fires automatically when:
 *                                matching autonomy_rules + tier ≤ 2 +
 *                                outside quiet hours + within budget +
 *                                not paused. R1 does NOT classify any
 *                                capability at L6 — enforcement chain
 *                                missing today.
 *   L7 — Auto-execute · self-heal  System-owned only. Internal recovery
 *                                with self_heal_runs ledger + budget
 *                                cap + kill-switch
 *   L8 — Auto-execute · sys-deploy System-owned only. Production-touching
 *                                deploys. Highest non-admin tier.
 *   L9 — Admin direct            Human admin via requireRole('admin').
 *                                Audited but not gateable. NOT recorded
 *                                in the capability registry — emerges
 *                                from the route layer.
 *
 * Owner ↔ level rules (locked, enforced by `assertOwnershipInvariant`):
 *   - owner='housy'   ⇒ level ≤ 1 (autonomous)
 *                       L2 with Housy participation only via explicit
 *                       user-initiated path (audit `actor='housy' +
 *                       initiated_by=<userId>`); the capability itself
 *                       still declares level ≤ 1.
 *   - owner='prumo'   ⇒ level ≤ 6
 *   - owner='shared'  ⇒ level ≤ 6 (never L7+)
 *   - level ≥ 7       ⇒ owner='system' (exclusive)
 *   - level === 9     must NOT appear in the registry (admin direct
 *                     route mutations are not capabilities).
 */

import { z } from 'zod';

export const AutonomyLevelSchema = z
  .number()
  .int()
  .min(0)
  .max(9)
  .describe(
    'Wave 354 Autonomy Ladder · 0–9 scale. See packages/shared/src/schemas/autonomy-level.ts for the full table.',
  );

export type AutonomyLevel = z.infer<typeof AutonomyLevelSchema>;

/**
 * Convenience constants. Use these instead of magic numbers in the
 * registry / migrations / lint fixtures so a future renumber stays
 * grep-discoverable.
 */
export const L0_OBSERVE = 0 as const;
export const L1_RECOMMEND = 1 as const;
export const L2_DRAFT = 2 as const;
export const L3_PROPOSE_HIGH = 3 as const;
export const L4_PROPOSE_MEDIUM = 4 as const;
export const L5_PROPOSE_LOW = 5 as const;
export const L6_AUTO_RULE = 6 as const;
export const L7_AUTO_SELF_HEAL = 7 as const;
export const L8_AUTO_SYS_DEPLOY = 8 as const;
export const L9_ADMIN_DIRECT = 9 as const;
